import { Routes, Route, Navigate } from 'react-router-dom';
import { SideNav } from './sideNav';
import { Header } from './header';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';

import { getAmbientSoloRoutes } from 'src/modules/ambient_solo/Routes';

import { getSuperAdminRoutes } from 'src/modules/SuperAdmin/Routes';
import { getTemplatesExchangeRoutes } from 'src/modules/TemplatesExchange/Routes';
import { getCelloWidgetRoutes } from 'src/modules/CelloWidget/Routes';
import { getCareConnectRoutes } from 'src/modules/appointments/Routes';
import { getSettingsRoutes } from 'src/modules/appointments/SettingsRoutes';
import { useAppRoutes } from 'src/pages';
import { DemoContainer } from 'src/pages/root';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import styled from '@emotion/styled';
import { Suspense } from 'react';
import { ScreenLoader } from 'src/components';
import { MobileNav } from './mobileNav';
import { useScreenSize } from '@insight-health-ai/roseate/utils';

const LayoutWrapper = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `
      --page-wrapper-content-height: calc(100dvh - 92px - 80px);
    `
      : `
      --page-wrapper-content-height: calc(100dvh - 92px);
    `}
  background-image: linear-gradient(
    to right,
    #e8f8ff,
    #e7f8ff,
    #e8f8ff,
    #ebf7ff,
    #e7f6ff,
    #eff7fd,
    #edf8ff
  );
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  height: 100dvh;
  width: 100dvw;
`;

export const Layout = () => {
  const { mediaMax } = useScreenSize();
  const { data: currentPractitioner, isSuperAdmin } =
    useGetCurrentPractitioner();
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: isPatientsModuleEntitled } =
    useFeatureEntitlements('patients-module');
  const { isEntitled: isFrontdeskModuleEntitled } =
    useFeatureEntitlements('frontdesk');
  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const routes = useAppRoutes();

  const getIndexRoute = () => {
    if (isPhoneTriageModuleEntitled) {
      return '/phone-triage';
    }
    if (isReferralsModuleEntitled) {
      return '/referrals';
    }
    if (hasIntakeAccess || hasFollowupAccess) {
      return '/appointments';
    }
    if (hasAmbientAccess) {
      return '/encounters';
    }
    if (isFrontdeskModuleEntitled) {
      return '/frontdesk';
    }
    if (isPatientsModuleEntitled) {
      return '/patients';
    }
    return '/settings';
  };

  const isMobile = mediaMax('sm');

  return (
    <LayoutWrapper
      isMobile={isMobile}
      className={`flex ${isMobile ? 'flex-col' : 'flex-row'} h-full w-full overflow-hidden`}
    >
      {!isMobile && <SideNav />}
      <div className="flex-shrink-1 flex flex-col h-full w-full overflow-hidden">
        <Header />
        <div className="flex-1 w-full p-4 overflow-y-auto">
          <Suspense fallback={<ScreenLoader />}>
            <Routes>
              {(hasIntakeAccess || hasFollowupAccess) && getCareConnectRoutes()}
              {hasAmbientAccess &&
                getAmbientSoloRoutes({
                  isMinimized: true,
                  setIsMinimized: () => {},
                })}
              {isSuperAdmin &&
                getSuperAdminRoutes({
                  isMinimized: true,
                })}
              {getSettingsRoutes({
                isMinimized: true,
              })}
              {getTemplatesExchangeRoutes()}
              {currentPractitioner?.cello_token && getCelloWidgetRoutes()}
              {routes}
              <Route
                path="*"
                element={<Navigate replace to={getIndexRoute()} />}
              />
            </Routes>
            <DemoContainer />
          </Suspense>
        </div>
      </div>
      {isMobile && <MobileNav />}
    </LayoutWrapper>
  );
};
